import type { IAgentMarkPreset } from '@/components/superscale-agent-mark/superscale-agent-mark'

/**
 * An agent role shown as a pixel-churn mark that reveals its role on hover (desktop) / tap (mobile).
 * Exactly one agent sets `defaultOpen` so its role pill is expanded at rest on DESKTOP (opened after
 * mount in lp-magic-section; mobile starts fully closed — the pill would overlay the creatives) — a
 * hint that the marks are labelled without forcing the user to hover first.
 */
export type IMagicAgent = { role: string; preset: IAgentMarkPreset; defaultOpen?: boolean }

// One preset per role, chosen for colour variety. First half flanks the stack on the left, second half
// on the right (see lp-magic-section). Mobile shows the first 3 per side, desktop (md) the first 5,
// large screens (xl) all 7 — so ORDER matters: the extra xl-only roles sit LAST in each half. The two
// repeated presets sit on the opposite side of (and far from) their twin. Keep exactly ONE
// `defaultOpen: true` within the first 5 of a half (desktop-only, so it may live outside the mobile slice).
export const MAGIC_AGENTS: IMagicAgent[] = [
    // left of the stack
    { role: 'Static Ads Designer', preset: 'searching' },
    { role: 'Creative Strategist', preset: 'thinking' },
    { role: 'Brand Researcher', preset: 'dream' },
    { role: 'Audience Researcher', preset: 'spark' },
    { role: 'Media Buyer', preset: 'ember' },
    { role: 'Concept Illustrator', preset: 'alert' },
    { role: 'Script Writer', preset: 'idle' },
    // right of the stack — the TOP-RIGHT mark carries defaultOpen: it sits on the outer edge, so its
    // opened pill extends across the gutter and never overlays the creatives.
    { role: 'Video Ad Editor', preset: 'working', defaultOpen: true },
    { role: 'Ad Copywriter', preset: 'focus' },
    { role: 'Motion Designer', preset: 'streaming' },
    { role: 'Campaign Planner', preset: 'waiting' },
    { role: 'Performance Analyst', preset: 'processing' },
    { role: 'A/B Test Analyst', preset: 'thinking' },
    { role: 'Trend Scout', preset: 'searching' },
]

/** How many agents per side are shown on mobile (the first N of each half). */
export const MAGIC_AGENTS_MOBILE_PER_SIDE = 3

/** How many agents per side are shown on md–xl desktops (xl+ shows the full half). */
export const MAGIC_AGENTS_DESKTOP_PER_SIDE = 5

// The stack creatives are NOT hardcoded here — they come from the per-landing-page carousel assets
// (resolveAssetSlug → landing carousel context), the same source the old landing carousel used.
